import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useEffect,
  useRef,
} from 'react';

import { Image } from '../image';
import { Typography } from '../typography';
import type { Tag as TagVariant } from '../typography/types';

import styles from './navigation-bar.scss';
import type { Variant } from './types';

type TitleProps = {
  variant: Variant;
  title?: string;
  titleComponent?: React.ReactNode;
  titleTag?: TagVariant;
};

const Title: React.FC<TitleProps> = ({ variant, title = '', titleComponent, titleTag }) => {
  const titleVariant =
    variant === 'title' || variant === 'title_with_icon' ? 'title-1-semibold' : 'title-2-semibold';

  return (
    <Typography className={styles.title} variant={titleVariant} tag={titleTag}>
      {titleComponent ?? title}
    </Typography>
  );
};

type DescriptionProps = {
  variant: Variant;
  description?: string;
  descriptionComponent?: React.ReactNode;
};

const Description: React.FC<DescriptionProps> = ({
  variant,
  description = '',
  descriptionComponent,
}) =>
  variant !== 'title' ? (
    <Typography className={styles.description} variant="caption-1-regular">
      {descriptionComponent ?? description}
    </Typography>
  ) : null;

type CenterProps = {
  variant: Variant;
  title?: string;
  titleComponent?: React.ReactNode;
  titleTag?: TagVariant;
  description?: string;
  descriptionComponent?: React.ReactNode;
  icon?: React.ReactNode;
  image?: React.ReactNode;
  center?: React.ReactNode;
};

const CenterContent: React.FC<CenterProps> = ({
  variant,
  title,
  titleComponent,
  titleTag,
  description,
  descriptionComponent,
  icon,
  image,
  center,
}) => {
  if (variant === 'logo') {
    return (
      <>
        {image ?? <Image name="logo_large" width="80" />}
        {center}
      </>
    );
  }

  if (variant === 'title_with_icon') {
    return (
      <span className={styles['title_with_icon']}>
        <Title
          variant={variant}
          title={title}
          titleComponent={titleComponent}
          titleTag={titleTag}
        />
        {icon ? icon : null}
      </span>
    );
  }

  return (
    <>
      <Title variant={variant} title={title} titleComponent={titleComponent} titleTag={titleTag} />
      <Description
        variant={variant}
        description={description}
        descriptionComponent={descriptionComponent}
      />
    </>
  );
};

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    title?: string;
    description?: string;
    variant?: Variant;
    titleTag?: TagVariant;
    transparent?: boolean;
    darkMode?: boolean;
    left?: React.ReactNode;
    center?: React.ReactNode;
    right?: React.ReactNode;
    icon?: React.ReactNode;
    image?: React.ReactNode;
    titleComponent?: React.ReactNode;
    descriptionComponent?: React.ReactNode;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-navigation-bar';

export const NavigationBar: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      title,
      description,
      variant = 'default',
      titleTag = 'h1',
      transparent,
      darkMode,
      left,
      center,
      right,
      icon,
      image,
      titleComponent,
      descriptionComponent,
      dataTestId = COMPONENT_ID,
      className,
      ...rest
    },
    ref,
  ) => {
    const layout = useRef<HTMLDivElement>(null);
    const leftRef = useRef<HTMLDivElement>(null);
    const centerRef = useRef<HTMLDivElement>(null);
    const rightRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const typographySelector = ':scope > [data-component-name="modulor-typography"]';

      const leftStyle =
        leftRef.current?.querySelectorAll(typographySelector).length ?? 0 > 0
          ? 'minmax(10%, 1fr)'
          : 'minmax(min-content, 1fr)';
      const rightStyle =
        rightRef.current?.querySelectorAll(typographySelector).length ?? 0 > 0
          ? 'minmax(10%, 1fr)'
          : 'minmax(min-content, 1fr)';

      if (layout.current) {
        layout.current.style.gridTemplateColumns =
          variant === 'default' ? `${leftStyle} auto ${rightStyle}` : `auto ${rightStyle}`;
      }
    }, [variant, left, right]);

    const classes = classNames(
      className,
      { [styles['two-columns']]: variant !== 'default' },
      { [styles['with-logo']]: variant === 'logo' },
      { [styles.darkMode]: darkMode },
      { [styles.transparent]: transparent },
    );

    return (
      <div
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        className={classes}
        ref={ref}
        {...rest}>
        <div ref={layout} data-component-name={`${COMPONENT_ID}-layout`} className={styles.layout}>
          <div
            ref={leftRef}
            className={styles.left}
            data-testid={`${dataTestId}-left-container`}
            data-component-name={`${COMPONENT_ID}-left-container`}>
            {left}
          </div>
          <div
            ref={centerRef}
            className={styles.center}
            data-testid={`${dataTestId}-center-container`}
            data-component-name={`${COMPONENT_ID}-center-container`}>
            <CenterContent
              variant={variant}
              title={title}
              description={description}
              titleTag={titleTag}
              center={center}
              icon={icon}
              image={image}
              titleComponent={titleComponent}
              descriptionComponent={descriptionComponent}
            />
          </div>
          <div
            ref={rightRef}
            className={styles.right}
            data-testid={`${dataTestId}-right-container`}
            data-component-name={`${COMPONENT_ID}-right-container`}>
            {right}
          </div>
        </div>
      </div>
    );
  },
);

NavigationBar.displayName = 'NavigationBar';
